import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import type { FoodSpot } from "../types";

const TYPE_ICON_CLASS: Record<FoodSpot["type"], string> = {
  Restaurant: "fa-utensils",
  "Street Food": "fa-cart-shopping",
  Cafe: "fa-mug-saucer",
  Bakery: "fa-bread-slice",
  Beverages: "fa-mug-hot",
  "Beach Shack": "fa-umbrella-beach",
};

function makeIcon(spot: FoodSpot) {
  const iconClass = TYPE_ICON_CLASS[spot.type];
  return L.divIcon({
    className: "custom-pin",
    html: `<div class="custom-pin__dot"><i class="fa-solid ${iconClass}"></i></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}

interface Props {
  spots: FoodSpot[];
  onSelect: (spot: FoodSpot) => void;
  selectedId?: string | null;
}

const MapView: React.FC<Props> = ({ spots, onSelect, selectedId }) => {
  useEffect(() => {
    document.body.classList.add("has-active-map");
    return () => document.body.classList.remove("has-active-map");
  }, []);

  const center = useMemo<[number, number]>(() => {
    if (spots.length === 0) return [11.2588, 75.7804];
    const latSum = spots.reduce((acc, s) => acc + s.coordinates.lat, 0);
    const lngSum = spots.reduce((acc, s) => acc + s.coordinates.lng, 0);
    return [latSum / spots.length, lngSum / spots.length];
  }, [spots]);

  return (
    <div className="map-wrap">
      <div className="map-wrap__bar">
        <i className="fa-solid fa-circle-info" aria-hidden="true" />
        Tap any pin to see the spot. Use the List view for full details.
      </div>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom={false}
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {spots.map((spot) => (
          <Marker
            key={spot.id}
            position={[spot.coordinates.lat, spot.coordinates.lng]}
            icon={makeIcon(spot)}
            zIndexOffset={spot.id === selectedId ? 1000 : 0}
          >
            <Popup>
              <div className="popup-card">
                <p className="popup-card__name">{spot.name}</p>
                <p className="popup-card__type">
                  {spot.type} &middot; {spot.cuisine}
                </p>
                <button
                  className="popup-card__cta"
                  onClick={() => onSelect(spot)}
                >
                  View details
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;